import { useEffect, useState, type FormEvent } from 'react';
import { collection, getDocs, orderBy, query } from 'firebase/firestore';
import toast from 'react-hot-toast';
import { Plus, Upload as UploadIcon, ShieldCheck, ShieldAlert, Loader2 } from 'lucide-react';
import { db, storage } from '@/lib/firebase';
import { ref, uploadBytesResumable } from 'firebase/storage';
import { callPublishRelease } from '@/lib/functions';
import {
  getReleaseBinaryContentType,
  isAllowedReleaseBinary,
  releaseFileName,
} from '@/lib/releaseBinary';
import { fmtBytes, fmtDate } from '@/lib/utils';
import { useT } from '@/lib/i18n';
import { Loader } from '@/components/ui/loader';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import { Input, inputClassName } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog } from '@/components/ui/dialog';
import { OsIcon } from '@/components/OsIcon';
import type { ReleaseArch, ReleaseDoc, ReleaseOs, ReleaseVariant } from '@/types/models';

const ARCH_BY_OS: Record<ReleaseOs, ReleaseArch[]> = {
  windows: ['x86_64'],
  macos: ['arm64', 'x86_64', 'universal'],
  linux: ['x86_64', 'arm64'],
};

/** Badge d'état de la vérification SHA-256 (trigger Storage `releases/**`). */
function Sha256Badge({ verified }: { verified: boolean | null | undefined }) {
  const { t } = useT();
  if (verified === true) return <Badge variant="success"><ShieldCheck className="mr-1 inline h-3 w-3" />{t('release.sha256.verified')}</Badge>;
  if (verified === false) return <Badge variant="danger"><ShieldAlert className="mr-1 inline h-3 w-3" />{t('release.sha256.mismatch')}</Badge>;
  return <Badge variant="neutral"><Loader2 className="mr-1 inline h-3 w-3" />{t('release.sha256.pending')}</Badge>;
}

export default function AdminReleases() {
  const { t } = useT();
  const [releases, setReleases] = useState<ReleaseDoc[]>([]);
  const [loading, setLoading] = useState(true);
  const [show, setShow] = useState(false);
  const [progress, setProgress] = useState(0);
  const [busy, setBusy] = useState(false);
  const [selectedOs, setSelectedOs] = useState<ReleaseOs>('windows');
  const [selectedArch, setSelectedArch] = useState<ReleaseArch>('x86_64');
  const [selectedVariant, setSelectedVariant] = useState<ReleaseVariant>('onefile');

  const changeOs = (os: ReleaseOs) => {
    setSelectedOs(os);
    setSelectedArch(ARCH_BY_OS[os][0]);
    if (os !== 'linux') setSelectedVariant('onefile');
  };

  const refresh = async () => {
    setLoading(true);
    const snap = await getDocs(query(collection(db, 'releases'), orderBy('publishedAt', 'desc')));
    setReleases(snap.docs.map(d => ({ id: d.id, ...d.data() } as ReleaseDoc)));
    setLoading(false);
  };
  useEffect(() => { refresh(); }, []);

  const publish = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const f = new FormData(e.currentTarget);
    const file = f.get('file') as File;
    if (!file) return toast.error(t('admin.releases.choose_file'));
    if (!isAllowedReleaseBinary(file)) return toast.error(t('admin.releases.invalid_binary'));

    setBusy(true);
    try {
      // 1. compute SHA-256 client-side
      const buf = await file.arrayBuffer();
      const hash = await crypto.subtle.digest('SHA-256', buf);
      const sha256 = Array.from(new Uint8Array(hash)).map(b => b.toString(16).padStart(2, '0')).join('');

      // 2. ask backend for upload path
      const { data } = await callPublishRelease({
        version:      String(f.get('version')),
        channel:      String(f.get('channel')) as 'stable' | 'beta',
        os:           String(f.get('os')) as ReleaseOs,
        arch:         String(f.get('arch')) as ReleaseArch,
        variant:      String(f.get('variant') ?? 'onefile') as ReleaseVariant,
        releaseNotes: String(f.get('notes')),
        sha256, sizeBytes: file.size, fileName: file.name,
      });

      // 3. upload binary to Storage at returned path
      const r = ref(storage, data.uploadPath);
      const task = uploadBytesResumable(r, file, { contentType: getReleaseBinaryContentType(file.name) });
      await new Promise<void>((resolve, reject) => {
        task.on('state_changed',
          s => setProgress((s.bytesTransferred / s.totalBytes) * 100),
          reject,
          () => resolve());
      });
      toast.success(t('admin.releases.published', { v: String(f.get('version')) }));
      setShow(false);
      refresh();
    } catch (e) { toast.error((e as Error).message); }
    finally { setBusy(false); setProgress(0); }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="font-display text-2xl text-ink-900 dark:text-white">{t('admin.releases.title')}</h1>
        <Button onClick={() => setShow(true)}><Plus className="h-4 w-4" /> {t('admin.releases.publish')}</Button>
      </div>

      {loading ? <Loader /> : (
        <div className="grid grid-cols-1 gap-3">
          {releases.map(r => (
            <Card key={r.id} className="flex items-center gap-4">
              <div className="grid h-10 w-10 place-items-center rounded-lg bg-brand-50 text-brand-600 dark:bg-brand-900/30 dark:text-brand-300">
                <OsIcon os={r.os} className="h-5 w-5" />
              </div>
              <div className="flex-1">
                <div className="font-medium text-ink-900 dark:text-white">
                  v{r.version}
                  <Badge variant="info" className="ml-2">{r.channel}</Badge>
                  <Badge variant="neutral" className="ml-2">{r.os ? t(`os.${r.os}` as 'os.windows') : r.id}</Badge>
                  <Badge variant="neutral" className="ml-2">{t(`arch.${r.arch ?? 'x86_64'}` as 'arch.x86_64')}</Badge>
                  {(r.variant ?? 'onefile') !== 'onefile' && <Badge variant="neutral" className="ml-2">{t(`admin.releases.variant.${r.variant}` as 'admin.releases.variant.onedir')}</Badge>}
                </div>
                <div className="text-xs text-ink-500">{r.fileName} · {fmtBytes(r.sizeBytes)} · {fmtDate(r.publishedAt)}</div>
              </div>
              <div className="flex flex-col items-end gap-1">
                <Sha256Badge verified={r.sha256Verified} />
                <code className="hidden font-mono text-[0.7rem] text-ink-500 sm:block" title={r.sha256Computed ? `${t('release.sha256.computed')} ${r.sha256Computed}` : undefined}>{r.sha256.slice(0, 12)}…</code>
              </div>
            </Card>
          ))}
        </div>
      )}

      <Dialog
        open={show}
        onClose={() => setShow(false)}
        title={t('admin.releases.modal.title')}
        footer={
          <>
            <Button type="button" variant="secondary" onClick={() => setShow(false)}>{t('common.cancel')}</Button>
            <Button type="submit" form="publish-release-form" disabled={busy}><UploadIcon className="h-4 w-4" /> {t('admin.releases.modal.cta')}</Button>
          </>
        }
      >
        <form id="publish-release-form" onSubmit={publish} className="grid gap-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>{t('admin.releases.modal.version')}</Label>
              <Input name="version" required pattern="^\d+\.\d+\.\d+$" placeholder="0.1.0" className="mt-1" />
            </div>
            <div>
              <Label>{t('admin.releases.modal.channel')}</Label>
              <select name="channel" required className={inputClassName(undefined, 'mt-1')}>
                <option value="stable">stable</option>
                <option value="beta">beta</option>
              </select>
            </div>
          </div>
          <div>
            <Label>{t('admin.releases.modal.os')}</Label>
            <select 
              name="os" 
              required 
              value={selectedOs} 
              onChange={e => changeOs(e.target.value as ReleaseOs)}
              className={inputClassName(undefined, 'mt-1')}
            >
              <option value="windows">{t('os.windows')}</option>
              <option value="macos">{t('os.macos')}</option>
              <option value="linux">{t('os.linux')}</option>
            </select>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>{t('admin.releases.modal.arch')}</Label>
              <select
                name="arch"
                required
                value={selectedArch}
                onChange={e => setSelectedArch(e.target.value as ReleaseArch)}
                className={inputClassName(undefined, 'mt-1')}
              >
                {ARCH_BY_OS[selectedOs].map(a => <option key={a} value={a}>{t(`arch.${a}` as 'arch.x86_64')}</option>)}
              </select>
            </div>
            <div>
              <Label>{t('admin.releases.modal.variant')}</Label>
              <select
                name="variant"
                required
                value={selectedVariant}
                onChange={e => setSelectedVariant(e.target.value as ReleaseVariant)}
                className={inputClassName(undefined, 'mt-1')}
                disabled={selectedOs !== 'linux'}
              >
                <option value="onefile">{t('admin.releases.variant.onefile')}</option>
                {selectedOs === 'linux' && <option value="onedir">{t('admin.releases.variant.onedir')}</option>}
              </select>
            </div>
          </div>
          <div>
            <Label>{t('admin.releases.modal.notes')}</Label>
            <textarea name="notes" required rows={4} className={inputClassName(undefined, 'mt-1')} placeholder={t('admin.releases.modal.notes_ph')} />
          </div>
          <div>
            <Label>{t('admin.releases.modal.binary')}</Label>
            <Input type="file" name="file" required className="mt-1" />
            <p className="mt-1 text-xs text-ink-500">
              {t(`admin.releases.modal.binary_hint.${selectedOs}` as 'admin.releases.modal.binary_hint.windows')}
              {' '}{t('admin.releases.modal.expected_name', { name: releaseFileName(selectedOs, selectedArch, selectedVariant) ?? '—' })}
            </p>
          </div>
          {busy && (
            <div>
              <div className="h-2 overflow-hidden rounded-full bg-slate-100 dark:bg-ink-700">
                <div className="h-full bg-brand-500 transition-all" style={{ width: `${progress}%` }} />
              </div>
              <div className="mt-1 text-xs text-ink-500">{progress.toFixed(0)}%</div>
            </div>
          )}
        </form>
      </Dialog>
    </div>
  );
}
